import { compact } from 'lodash'
import { useCallback, useMemo } from 'react'
import { BaseInputWrapperProps } from '~/@/components/inputs/base/types'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { OptionGroup } from '~/@/components/inputs/selection/utils'
import { RoleType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Role } from '../settings/types'

interface RolesDropdownProps extends BaseInputWrapperProps {
  roles: Role[]
  selectedRoleIds: string[]
  onChange: (selectedRoleIds: string[]) => void
  onBlur: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * Renders the multiselect autocomplete component for selecting roles for a user
 */
export function RolesDropdown(props: RolesDropdownProps) {
  const { roles, selectedRoleIds, onChange, onBlur } = props
  const { t } = useTypedTranslation()

  const getOptionDisabled = useCallback(
    (optionValue: string) => {
      const option = roles.find((role) => role.id === optionValue)

      const selectedOptions = compact(
        selectedRoleIds.map((roleId) => roles.find((role) => role.id === roleId)),
      )
      if (selectedOptions.some((selectedOption) => selectedOption.roleType === RoleType.ADMIN)) {
        // Disable all options that are non-Admin options if the admin option has been selected
        return option?.roleType !== RoleType.ADMIN
      }
      return false
    },
    [roles, selectedRoleIds],
  )

  // Filter out the internal Doss role as an option, put the Admin roles at the top, sort the
  // other options alphabetically
  const roleOptions: OptionGroup[] = useMemo(() => {
    const adminRoles = roles.filter((role) => role.roleType === RoleType.ADMIN)

    const nonAdminRoles = roles
      .filter((role) => role.roleType === RoleType.MEMBER)
      .sort((a, b) => a.name.localeCompare(b.name))

    return [
      {
        label: `${t('settings.admin')} (${t('roles.access_to_everything')})`,
        options: adminRoles.map((role) => ({
          label: role.name,
          value: role.id,
          disable: getOptionDisabled(role.id),
        })),
      },
      {
        label: t('settings.member'),
        options: nonAdminRoles.map((role) => ({
          label: role.name,
          value: role.id,
          disable: getOptionDisabled(role.id),
        })),
      },
    ]
  }, [getOptionDisabled, roles, t])

  return (
    <Dropdown
      {...props}
      inputProps={{
        onBlur,
      }}
      isMultiSelect
      onChange={(newSelection) => {
        const selectedRoles = roles.filter((role) => newSelection?.includes(role.id))

        // Check if any of the newly selected roles is an admin
        const hasAdmin = selectedRoles.some((role) => role.roleType === RoleType.ADMIN)

        if (hasAdmin) {
          // If there's an admin, only keep the admin roles
          const adminRoles = selectedRoles.filter((role) => role.roleType === RoleType.ADMIN)
          onChange(adminRoles.map((role) => role.id))
        } else {
          // If no admin, keep all selected roles
          onChange(selectedRoles.map((role) => role.id))
        }
      }}
      options={roleOptions.flatMap((group) => group.options)}
      values={selectedRoleIds}
    />
  )
}
